import { Grid, InputAdornment, Typography } from '@mui/material';
import { find } from 'lodash';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';

import { useDeviceAlertConfigurationOptions, usePostAddNewAlert } from '@/api/customSensorAlerts';
import { useCurrentSensorConfiguration } from '@/api/device';
import { useDevice } from '@/api/devices';
import { ConvertibleUnitPreferenceTypes, useDisplayPreferences } from '@/api/userAccount';
import { DatumUnit, renderUnit, unitInContext } from '@/components/data/Datum';
import { FormButtonGroup } from '@/components/form/FormButtonGroup';
import { FormInputText } from '@/components/form/FormInputText';
import { FormSelect } from '@/components/form/FormSelect';
import { GenericSkeleton } from '@/components/loaders/GenericSkeleton';
import { AlertEditFormValues } from '@/components/spotter/notificationTab/CustomSensorAlertsSection';
import { ResetUpdateButtonGroup } from '@/components/userAccount/ResetUpdateButtonGroup';
import {
    composeAlertOperatorOptions,
    composeAlertTypeOptions,
    composeSensorPositionOptions,
    triggerQuantityType,
} from '@/utils';

type SensorAlertEditFormProps = {
    onReset: () => void;
    isOpen?: boolean;
    close?: () => void;
    isEditFlow?: boolean;
};

/**
 * Triggers have a narrower range of unit representations than we support; narrow to trigger-supported versions.
 */
function unitForTrigger(unit?: DatumUnit) {
    switch (unit) {
        case 'C':
            return '°C';
        case 'F':
            return '°F';
        default:
            return unit;
    }
}

export const SensorAlertEditForm = ({
    onReset = () => {},
    close = () => {},
    isEditFlow = false,
}: SensorAlertEditFormProps) => {
    const elementRef = useRef<HTMLDivElement>(null);
    const thresholdInputRef = useRef<HTMLInputElement>(null);
    const alertTypeInputRef = useRef<{ node: HTMLInputElement }>(null);
    const { data: device, isPending: isDevicesPending } = useDevice();
    const spotterId = device?.spotterId;
    const { mutateAsync: postNewAlert } = usePostAddNewAlert();
    const { data: displayPreferences } = useDisplayPreferences();
    const { data: sensorConfig } = useCurrentSensorConfiguration({ spotterId: spotterId ?? null });

    const { data: alertOptions, isPending: isAlertOptionPending } = useDeviceAlertConfigurationOptions({
        spotterId: spotterId!,
        config: { enabled: !!spotterId },
    });
    const [sensorPositionAtOpen, setSensorPositionAtOpen] = useState('');
    const { control, handleSubmit, formState, watch, getValues, setValue } = useFormContext<AlertEditFormValues>();
    const currentlySelectedAlertType = watch('alertType');
    const sensorPositionOptions = composeSensorPositionOptions(
        currentlySelectedAlertType,
        alertOptions?.data.maxSensorPosition ?? 0,
        sensorConfig
    );

    const triggerUnitType = find(alertOptions?.data?.triggerTypes, { trigger_type: currentlySelectedAlertType })
        ?.display_info.unitType;
    const unitType =
        triggerUnitType &&
        triggerQuantityType({
            trigger_type: currentlySelectedAlertType,
            unitType: triggerUnitType,
        });

    const triggerUnits = useMemo(() => {
        const preferredUnit =
            unitType && displayPreferences && unitType in displayPreferences
                ? unitForTrigger(displayPreferences[unitType as ConvertibleUnitPreferenceTypes])
                : undefined;
        const supportedUnitTypes = alertOptions?.data?.supportedUnitTypes;
        // a little awkward but we're getting some supportedUnitTypes in camel case and others in snake so we want to try both.
        // unitType is normalized to our quantity-sensitive units, triggerUnitType is the original we got from the backend.
        const supportedUnits = (supportedUnitTypes?.[unitType ?? ''] ?? supportedUnitTypes?.[triggerUnitType ?? ''])
            ?.supportedUnits;
        if (preferredUnit && supportedUnits && !supportedUnits.includes(preferredUnit)) {
            console.error(
                `Preferred unit for quantity ${unitType} (${preferredUnit}) is not supported by trigger ${currentlySelectedAlertType}`
            );
            return supportedUnits[0] as DatumUnit;
        }
        return preferredUnit ?? (supportedUnits?.[0] as DatumUnit);
    }, [
        alertOptions?.data?.supportedUnitTypes,
        currentlySelectedAlertType,
        displayPreferences,
        triggerUnitType,
        unitType,
    ]);

    const renderedUnit = useMemo(() => {
        if (!unitType || !triggerUnits) {
            return '';
        }
        return renderUnit(unitInContext(unitType, triggerUnits, device) ?? triggerUnits);
    }, [device, triggerUnits, unitType]);

    const onNewAlertSubmit = async (formData: AlertEditFormValues) => {
        if (!spotterId) {
            console.warn('AddNewSensorAlert: onNewAlertSubmit: No Spotter selected');
            return;
        }
        try {
            const currentTriggerType = alertOptions?.data.triggerTypes.find(
                (triggerType) => triggerType.trigger_type === formData.alertType
            );
            if (!currentTriggerType) {
                console.warn('AddNewSensorAlert: onNewAlertSubmit: Cannot find current trigger type');
                return;
            }
            const allSupportedUnits = alertOptions?.data.supportedUnitTypes[currentTriggerType.display_info.unitType];
            if (!allSupportedUnits) {
                console.warn('AddNewSensorAlert: onNewAlertSubmit: Cannot find supportedUnitTypes');
                return;
            }

            const triggerDetails = {
                isSmartMooring: currentTriggerType.trigger_type.includes('smartmooring'),
                trigger_action: 'email',
                trigger_type: formData.alertType,
                displayName: currentTriggerType.display_info.name,
                operator: formData.logicalOperator,
                units: triggerUnits ?? '',
                unitType: currentTriggerType.display_info.unitType,
                value: formData.thresholdValue,
                unitPrecisionInfo: {
                    decimalPlaces: allSupportedUnits.decimalPlaces,
                    supportedUnits: allSupportedUnits.supportedUnits,
                },
                sensorPosition: formData.sensorPosition === '' ? undefined : parseInt(formData.sensorPosition, 10),
            };

            await postNewAlert({
                spotterId,
                trigger_type: formData.alertType,
                trigger_action: 'email',
                enabled: true,
                trigger_details: triggerDetails,
            });
        } catch (err) {
            console.error(err);
        }
        onReset();
        close();
    };

    useEffect(() => {
        if (!isDevicesPending && !isAlertOptionPending) {
            setTimeout(() => {
                elementRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }, 200);
        }
    }, [isDevicesPending, isAlertOptionPending]);

    useEffect(() => {
        if (currentlySelectedAlertType) {
            thresholdInputRef.current?.select();
            thresholdInputRef.current?.focus();
        }
    }, [currentlySelectedAlertType]);

    if (isDevicesPending || isAlertOptionPending) {
        return <GenericSkeleton />;
    }

    return (
        <Grid
            container
            gap={2}
            justifyContent="flex-end"
            sx={{ my: 2, width: 'min-content', alignSelf: 'center' }}
            ref={elementRef}
        >
            <Grid size={{ xs: 12 }}>
                <FormSelect
                    name="alertType"
                    control={control}
                    label="Alert type"
                    options={composeAlertTypeOptions(alertOptions?.data, device)}
                    inputFieldProps={{
                        inputRef: alertTypeInputRef,
                        disabled: isEditFlow,
                        id: 'new-custom-alert-alert-type',
                        type: 'text',
                        size: 'small',
                        fullWidth: true,
                    }}
                    controllerProps={{
                        rules: {
                            required: 'Field is required',
                        },
                    }}
                />
            </Grid>
            <Grid size={{ xs: 12 }}>
                <FormButtonGroup
                    name="logicalOperator"
                    control={control}
                    label="Comparison operator"
                    toggleButtons={composeAlertOperatorOptions(alertOptions?.data)}
                    buttonRenderer={({ label }) => (
                        <Typography variant="h4" sx={{ width: '3em' }}>
                            {label}
                        </Typography>
                    )}
                    controllerProps={{
                        rules: {
                            required: 'Field is required',
                        },
                    }}
                />
            </Grid>
            <Grid size={{ xs: 12 }}>
                <FormInputText
                    name="thresholdValue"
                    control={control}
                    label="Threshold value"
                    inputFieldProps={{
                        id: 'new-custom-alert-threshold-value',
                        size: 'small',
                        fullWidth: true,
                        slotProps: {
                            input: {
                                endAdornment: <InputAdornment position="end">{renderedUnit}</InputAdornment>,
                                inputRef: thresholdInputRef,
                            },
                        },
                        onKeyDown: (event) => {
                            if (
                                event.key.length === 1 &&
                                !(event.ctrlKey || event.altKey || event.metaKey) &&
                                !event.key.match(/[-.\d]/)
                            ) {
                                event.preventDefault();
                                return;
                            }
                        },
                    }}
                    controllerProps={{
                        rules: {
                            required: 'Field is required',
                        },
                    }}
                />
            </Grid>
            <Grid size={{ xs: 12 }}>
                {currentlySelectedAlertType?.includes('smartmooring') && (
                    <FormSelect
                        name="sensorPosition"
                        control={control}
                        label="Sensor Position"
                        options={sensorPositionOptions}
                        inputFieldProps={{
                            id: 'new-custom-alert-sensor-position',
                            type: 'text',
                            size: 'small',
                            fullWidth: true,
                            slotProps: {
                                select: {
                                    onOpen: () => {
                                        setSensorPositionAtOpen(getValues().sensorPosition);
                                        setValue('sensorPosition', '');
                                    },
                                    onClose: () => {
                                        if (!getValues().sensorPosition && sensorPositionAtOpen) {
                                            setValue('sensorPosition', sensorPositionAtOpen);
                                        }
                                        setSensorPositionAtOpen('');
                                    },
                                },
                            },
                        }}
                        controllerProps={{
                            rules: {
                                validate: (value: string) => {
                                    const isSpotterAlert =
                                        !currentlySelectedAlertType.includes('smartmooring') && value === '';
                                    const isSmAlertWithValue =
                                        currentlySelectedAlertType.includes('smartmooring') && value !== '';
                                    return isSpotterAlert || isSmAlertWithValue || 'Field is required';
                                },
                            },
                        }}
                    />
                )}
            </Grid>
            <Grid size={{ xs: 12 }} justifyContent="flex-end" display="flex">
                <ResetUpdateButtonGroup
                    onReset={onReset}
                    onSubmit={handleSubmit(onNewAlertSubmit)}
                    isResetDisabled={!formState.isDirty || formState.isSubmitting}
                    isUpdateDisabled={!formState.isDirty || formState.isSubmitting}
                    isSubmitting={formState.isSubmitting}
                    containerStyle={{ justifyContent: { xs: 'flex-end', sm: 'flex-start' }, mt: 1, mb: 0 }}
                    updateLabel={isEditFlow ? 'Update' : 'Create'}
                />
            </Grid>
        </Grid>
    );
};
